import { FunctionComponent } from "react";
import { Link } from "react-router-dom";

import { faSparkles } from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components";
import { Tooltip } from "@/components/Tooltip/Tooltip";
import { LinkedinIcon } from "@/components/LinkedinIcon/LinkedinIcon";
import { ContactChannels } from "@/components/ContactChannels";
import { InfoCardPopover, InfoCardPopoverRow } from "@/components/InfoCardPopover";

import { MyConnections } from "@/pages/Public/pages/Login/schema/login";

import type { ContactChannelData } from "@/components/ContactChannels/types";

export const EnrichedNameCell: FunctionComponent<{
	row: MyConnections;
	handleCopyClick: (value: string) => void;
	ts: (key: string) => string;
}> = ({ row, handleCopyClick, ts: _ts }) => {
	const channelData: ContactChannelData = {
		id: row.prospectId!,
		name: `${row.firstName} ${row.lastName}`,
		firstName: row.firstName,
		middleName: null,
		lastName: row.lastName,
		gender: null,
		email: row.email ?? null,
		emailStatus: row.emailStatus ?? null,
		phone: row.phone ?? null,
		phoneStatus: row.phoneStatus ?? null,
		linkedinUrl: row.linkedinUrl ?? null,
		bookingLink: null,
		pendingEmails: [],
	};

	return (
		<div className="relative flex w-full max-w-[250px] flex-row justify-between items-center">
			<Tooltip
				content={
					<InfoCardPopover
						kind="lead"
						onCopy={handleCopyClick}
						rows={
							[
								{ label: "Name", value: `${row.firstName} ${row.lastName}` },
								{ label: "Job title", value: row.title },
								{ label: "Location", value: row.country },
								{ label: "Persona prio", value: row.overallScore },
							] satisfies InfoCardPopoverRow[]
						}
					/>
				}
			>
				<div className="cursor-pointer mr-1 min-w-0 flex-1 max-w-[140px] overflow-hidden">
					<span className="block min-w-0 flex-1 truncate rounded-[5px] px-[3px] py-[1px] -mx-[3px] -my-[1px] hover:bg-gray-50 transition-colors">
						{row.firstName} {row.lastName}
					</span>
				</div>
			</Tooltip>

			<div className="flex flex-row items-center shrink-0">
				<ContactChannels data={channelData} domain="contact" onCopy={handleCopyClick} />
			</div>
		</div>
	);
};

export const UnenrichedNameCell: FunctionComponent<{
	row: MyConnections;
	enrichEnabled: boolean;
	onEnrichRow: (id: number) => void;
	ts: (key: string) => string;
}> = ({ row, enrichEnabled, onEnrichRow, ts }) => (
	<div className="relative flex w-full max-w-[250px] flex-row justify-between items-center">
		<div className="cursor-pointer mr-1 min-w-0 flex-1 max-w-[140px] overflow-hidden">
			<span className="block min-w-0 flex-1 truncate rounded-[5px] px-[3px] py-[1px] -mx-[3px] -my-[1px] hover:bg-gray-50 transition-colors">
				{row.firstName} {row.lastName}
			</span>
		</div>

		<div className="flex flex-row items-center">
			{row.linkedinUrl && (
				<Tooltip content={row.linkedinUrl}>
					<Link
						className="flex h-[20px] cursor-pointer items-center"
						target="_blank"
						to={row.linkedinUrl}
						onClick={(e) => e.stopPropagation()}
					>
						<LinkedinIcon status="checked" />
					</Link>
				</Tooltip>
			)}

			<Tooltip content={ts("myConnections.enrichDisabledTooltip")} disabled={enrichEnabled}>
				<button
					className={
						enrichEnabled
							? "inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded text-blue-600 hover:bg-blue-50 cursor-pointer"
							: "inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded text-gray-400 cursor-not-allowed opacity-60"
					}
					disabled={!enrichEnabled}
					type="button"
					onClick={enrichEnabled ? () => onEnrichRow(row.id) : undefined}
				>
					<Icon className="h-2.5 w-2.5" icon={faSparkles} />
					{ts("myConnections.inlineEnrich")}
				</button>
			</Tooltip>
		</div>
	</div>
);
